import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { RESULT_TYPES } from '../../data/quizData';
import TidyCalEmbed from './TidyCalEmbed';

const {
  FiPhone, FiMapPin, FiAlertTriangle, FiDollarSign,
  FiSearch, FiCheckCircle, FiXCircle, FiMessageSquare
} = FiIcons;

const CalendarSection = ({ resultType }) => {
  const isOverpaying = resultType === RESULT_TYPES.OVERPAYING;
  return (
    <div className="space-y-6">
      {isOverpaying ? (
        <>
          <p className="text-sm text-gray-500 text-center font-medium uppercase tracking-wide">
            Choose your preferred appointment type
          </p>
          <div className="space-y-8">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiPhone} className="text-blue-500" />
                Discovery Call (Phone or Video)
              </h4>
              <TidyCalEmbed dataPath="pinoygeneralinsurance/discovery-call-phone-or-video" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <SafeIcon icon={FiMapPin} className="text-emerald-500" />
                In-Office Strategy Session
              </h4>
              <TidyCalEmbed dataPath="pinoygeneralinsurance/in-office-strategy-session" />
            </div>
          </div>
        </>
      ) : (
        <TidyCalEmbed dataPath="pinoygeneralinsurance/discovery-call-phone-or-video" />
      )}
    </div>
  );
};

const SectionItem = ({ item, isChecklist, iconType }) => (
  <li className="flex items-start gap-3">
    <span className="mt-1 flex-shrink-0">
      {isChecklist ? (
        <SafeIcon icon={FiCheckCircle} className="text-emerald-500" />
      ) : iconType === 'cross' ? (
        <SafeIcon icon={FiXCircle} className="text-red-500" />
      ) : (
        <span className="block w-2 h-2 mt-1.5 rounded-full bg-blue-500" />
      )}
    </span>
    <span className="text-gray-700 leading-relaxed">{item}</span>
  </li>
);

const ResultScreen = ({ resultType, userData }) => {
  const firstName = userData?.firstName || 'Friend';

  const iconMap = {
    [RESULT_TYPES.UNDERINSURED]: FiAlertTriangle,
    [RESULT_TYPES.OVERPAYING]: FiDollarSign,
    [RESULT_TYPES.GAPS]: FiSearch,
  };

  const content = {
    [RESULT_TYPES.UNDERINSURED]: {
      color: 'red',
      emoji: '⚠️',
      headlineTop: `You're Likely Underinsured,`,
      headlineName: firstName,
      intro: "Based on your answers, you may not have adequate protection if an accident occurs. Here's what concerns us:",
      sections: [
        { title: "Your Situation:", items: ["You currently have liability-only coverage", "This protects OTHER people, but not YOU or your vehicle", "If you're in an at-fault accident, you could pay thousands out of pocket", "If your car is damaged by weather, theft, or vandalism - you're not covered"] },
        { title: "The Risk:", text: "California drivers pay an average of $4,500 to repair collision damage. Without comprehensive and collision coverage, that's 100% your responsibility." },
        { title: "What You Need:", intro: "A free 15-minute coverage review to:", items: ["Identify exactly what you're missing", "See what full coverage would actually cost (might surprise you!)", "Understand your options without pressure", "Get a quote in minutes"], isChecklist: true },
        { title: "Specific Recommendations:", items: ["Add Comprehensive coverage (theft, weather, vandalism)", "Add Collision coverage (accident damage to YOUR car)", "Consider Uninsured Motorist coverage (13% of CA drivers have no insurance!)", "Review liability limits (state minimum may not be enough)"] }
      ],
      testimonial: { quote: "I thought I was saving money with liability-only. After my coverage review with Felix, I realized I was one accident away from financial disaster. Full coverage was only $40 more per month - totally worth it!", author: "Maria S., Cerritos" }
    },
    [RESULT_TYPES.OVERPAYING]: {
      color: 'emerald',
      emoji: '💰',
      headlineTop: `You Could Be Overpaying,`,
      headlineName: firstName,
      intro: "Based on your answers, you likely have good coverage but may be paying more than necessary. Here's why:",
      sections: [
        { title: "Your Situation:", items: ["You have full coverage (great!)", "You haven't reviewed your policy in 3+ years", "You're concerned about the cost", "California insurance rates have changed significantly since you last shopped"] },
        { title: "The Opportunity:", text: "Most drivers who haven't reviewed their policy in 3+ years are overpaying by $300-800 annually. Insurance companies rarely lower your rate automatically - even if you qualify for new discounts!" },
        { title: "Discounts You Might Be Missing:", items: ["Safe driver discount (no accidents in 3+ years)", "Multi-policy bundle (auto + home = 15-25% savings)", "Loyalty discount (been with same company? Ironically, switching often saves MORE!)", "Good student discount (if you have teens on policy)", "Low mileage discount (working from home? Driving less?)", "Paid-in-full discount (pay annually vs monthly)"], isChecklist: true },
        { title: "What We'll Do:", intro: "In your free 15-minute review, we'll:", items: ["Compare your current coverage across 15+ carriers", "Find every discount you qualify for", "Show you the exact dollar savings", "Keep your coverage the same (or better!) for less money"], isChecklist: true },
        { title: "Average Savings:", text: "Our clients who switch typically save $400-900 per year. That's $33-75 per month back in your pocket." }
      ],
      testimonial: { quote: "I was with the same company for 8 years. Felix found me the SAME coverage for $650 less per year. Took 15 minutes. Easiest money I ever saved!", author: "Robert T., Artesia" }
    },
    [RESULT_TYPES.GAPS]: {
      color: 'blue',
      emoji: '🔍',
      headlineTop: `You May Have Coverage Gaps,`,
      headlineName: firstName,
      intro: "Based on your answers, you're not quite sure what your current policy includes or if it's adequate. You're not alone - 67% of drivers don't fully understand their auto insurance!",
      sections: [
        { title: "Your Situation:", items: ["You're uncertain about your coverage level", "You may not understand all the terms in your policy", "You're not sure if you're properly protected", "It's been a while since anyone explained your options"] },
        { title: "Common Coverage Gaps We Find:", items: ["Underinsured Motorist: 1 in 7 California drivers has no insurance. If they hit you, who pays?", "Rental Reimbursement: If your car is in the shop for weeks, how will you get to work?", "Gap Insurance: If you total a financed car, your loan might exceed the car's value", "Roadside Assistance: Towing can cost $150+ per incident", "Medical Payments: Who covers your injuries if you're at fault?"], iconType: 'cross' },
        { title: "Why This Matters:", text: "Most people discover coverage gaps AFTER an accident - when it's too late. A quick review now could save you thousands later." },
        { title: "What We'll Cover in 15 Minutes:", items: ["Plain-English explanation of what you currently have", "Walk through your actual policy declarations page", "Identify any gaps or overlaps", "Answer all your questions (no stupid questions!)", "Recommend exactly what you need (and what you don't)"], isChecklist: true },
        { title: "Our Promise:", items: ["No pressure, no sales pitch", "Just clear answers and honest guidance", "You'll leave knowing exactly what you have and why"] }
      ],
      testimonial: { quote: "I had no idea what my policy actually covered. Felix walked me through every line item. Found out I was paying for coverage I didn't need AND missing coverage I did need. Super helpful!", author: "Linda P., Cerritos" }
    }
  };

  const data = content[resultType] || content[RESULT_TYPES.GAPS];
  const IconComponent = iconMap[resultType] || FiSearch;

  const colorClasses = {
    red:     { bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     icon: 'text-red-500',     name: 'text-red-500',     badge: 'bg-red-100 text-red-700' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', icon: 'text-emerald-500', name: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-800',    border: 'border-blue-200',    icon: 'text-blue-500',    name: 'text-blue-500',   badge: 'bg-blue-100 text-blue-700' },
  };
  const theme = colorClasses[data.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden"
    >
      {/* Hero Header */}
      <div className={`${theme.bg} border-b ${theme.border} px-8 pt-10 pb-12 md:px-12 md:pt-14 text-center`}>
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 ${theme.badge}`}>
          <SafeIcon icon={IconComponent} className="text-base" />
          Your Coverage Analysis
        </div>

        <div className="text-5xl mb-4">{data.emoji}</div>

        <h1 className={`text-4xl md:text-5xl font-extrabold leading-tight mb-2 ${theme.text} tracking-tight`}>
          {data.headlineTop}
        </h1>
        <h1 className={`text-4xl md:text-5xl font-extrabold leading-tight mb-6 ${theme.name} tracking-tight`}>
          {data.headlineName}
        </h1>

        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          {data.intro}
        </p>
      </div>

      {/* Body */}
      <div className="p-8 md:p-10 space-y-10">
        {data.sections.map((section, idx) => (
          <div key={idx}>
            <h3 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h3>
            {section.text && <p className="text-gray-700 leading-relaxed">{section.text}</p>}
            {section.intro && <p className="text-gray-700 mb-3">{section.intro}</p>}
            {section.items && (
              <ul className="space-y-3">
                {section.items.map((item, i) => (
                  <SectionItem key={i} item={item} isChecklist={section.isChecklist} iconType={section.iconType} />
                ))}
              </ul>
            )}
          </div>
        ))}

        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Next Step:</h3>
            <p className="text-gray-600">
              Book your free 15-minute review below. We'll analyze your current policy and show you exactly what's missing or where you can save.
            </p>
          </div>
          <CalendarSection resultType={resultType} />
        </div>

        <div className="bg-blue-50 rounded-2xl p-8 relative">
          <SafeIcon icon={FiMessageSquare} className="absolute top-6 left-6 text-4xl text-blue-200 opacity-50" />
          <div className="relative z-10 pl-6">
            <p className="text-lg text-gray-800 italic mb-4">"{data.testimonial.quote}"</p>
            <p className="font-semibold text-blue-900">- {data.testimonial.author}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultScreen;